// src/scripts/useScripts/useAuditKpis.js
import { ref, unref } from "vue";
import { db } from "@/firebase";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
} from "firebase/firestore";

/**
 * useAuditKpis
 *
 * Pulls Admin Audit Score and Venue Audit Score from:
 *
 * venues/{venueId}/audits/{dayKey}
 *
 * Example:
 * venues/o-bar-zoo-watford/audits/2026-06-02
 *
 * WTD average rules:
 * - Uses the selected dashboard week, not the real current date only.
 * - If viewing a past week, WTD runs Monday -> selected week ending.
 * - If viewing the current week, WTD runs Monday -> today.
 * - Open days are calculated from closedDays.
 * - Missing audit docs on open days count as 0%.
 * - hasAdminAuditWTD / hasVenueAuditWTD are true when any audit exists in the selected WTD window.
 *
 * Important:
 * - KpiMiniCard.vue only displays the values.
 * - This composable is what pulls Firestore data.
 */
export function useAuditKpis(opts = {}) {
  const weekEndingRef = opts.weekEnding;

  const loading = ref(false);
  const error = ref("");

  const adminAvgWTD = ref(0);
  const venueAvgWTD = ref(0);

  const hasAdminAuditWTD = ref(false);
  const hasVenueAuditWTD = ref(false);

  const WEEKDAY_KEYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

  function toLondonYmd(date = new Date()) {
    return new Intl.DateTimeFormat("en-CA", {
      timeZone: "Europe/London",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(date);
  }

  function normalizeYmdInput(value) {
    if (!value) return "";

    if (value instanceof Date) {
      return toLondonYmd(value);
    }

    const str = String(value).trim();

    // Handles "2026-06-02" or "2026-06-02T00:00:00.000Z"
    const isoMatch = str.match(/^(\d{4}-\d{2}-\d{2})/);
    if (isoMatch) return isoMatch[1];

    return str;
  }

  function parseYmdToUtcMidday(ymd) {
    const [y, m, d] = String(ymd).split("-").map(Number);
    return new Date(Date.UTC(y, m - 1, d, 12, 0, 0));
  }

  function addDaysYmd(ymd, deltaDays) {
    const dt = parseYmdToUtcMidday(ymd);
    dt.setUTCDate(dt.getUTCDate() + deltaDays);
    return toLondonYmd(dt);
  }

  function getLondonWeekStartFromAnyYmd(ymd) {
    const cleanYmd = normalizeYmdInput(ymd);
    const base = parseYmdToUtcMidday(cleanYmd);

    const jsDay = base.getUTCDay(); // 0 = Sun, 1 = Mon, etc.
    const delta = (jsDay + 6) % 7; // Monday = 0

    base.setUTCDate(base.getUTCDate() - delta);

    return toLondonYmd(base);
  }

  function weekdayKeyFromYmd(ymd) {
    const dt = parseYmdToUtcMidday(ymd);
    const jsDay = dt.getUTCDay(); // 0 = Sun, 1 = Mon, etc.

    const map = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];
    return map[jsDay];
  }

  function clampPercent(n) {
    const x = Number(n);

    if (!Number.isFinite(x)) return 0;
    if (x < 0) return 0;
    if (x > 100) return 100;

    return Math.round(x);
  }

  function normalizeClosedDays(obj) {
    const out = {};

    for (const k of WEEKDAY_KEYS) {
      out[k] = obj?.[k] === true;
    }

    return out;
  }

function buildOpenDaysBetween(weekStartYmd, endYmd, closedDaysObj) {
  const open = [];

  if (!weekStartYmd || !endYmd) return open;

  let cursor = weekStartYmd;

  while (cursor <= endYmd) {
    const wk = weekdayKeyFromYmd(cursor);

    // true = include this day in the audit score
    // false = exclude this day from the audit score
    if (closedDaysObj?.[wk] === true) {
      open.push(cursor);
    }

    cursor = addDaysYmd(cursor, 1);
  }

  return open;
}

  async function loadWeeklyClosedDays(venueId, weekStartKey) {
    const weekDocRef = doc(db, "venues", venueId, "auditWeeks", weekStartKey);
    const snap = await getDoc(weekDocRef);

    if (!snap.exists()) return null;

    const data = snap.data() || {};
    return normalizeClosedDays(data.closedDays || null);
  }

  function getClosedDaysFromAuditDocs(docs) {
    for (const docSnap of docs) {
      const data = docSnap.data() || {};

      if (data.closedDays) {
        return normalizeClosedDays(data.closedDays);
      }
    }

    return normalizeClosedDays(null);
  }

  async function loadAuditWeekToDate(venueId) {
    loading.value = true;
    error.value = "";

    adminAvgWTD.value = 0;
    venueAvgWTD.value = 0;
    hasAdminAuditWTD.value = false;
    hasVenueAuditWTD.value = false;

    try {
      if (!venueId) return;

      const todayKey = toLondonYmd(new Date());

      const selectedWeekEndingKey = normalizeYmdInput(unref(weekEndingRef)) || todayKey;
      const weekStartKey = getLondonWeekStartFromAnyYmd(selectedWeekEndingKey);

      /**
       * Critical fix:
       *
       * If the dashboard is viewing a previous week, stop the query at that
       * selected week ending date.
       *
       * Previously this used today's real date, which allowed future/current
       * audit documents to leak into old dashboard weeks.
       */
      const wtdEndKey =
        selectedWeekEndingKey < todayKey
          ? selectedWeekEndingKey
          : todayKey;

      const auditsRef = collection(db, "venues", venueId, "audits");

      const qAudits = query(
        auditsRef,
        where("dayKey", ">=", weekStartKey),
        where("dayKey", "<=", wtdEndKey),
        orderBy("dayKey", "asc")
      );

      const snap = await getDocs(qAudits);

      const byDay = {};

      snap.forEach((docSnap) => {
        const data = docSnap.data() || {};
        const dayKey = data.dayKey || docSnap.id;

        byDay[dayKey] = {
          id: docSnap.id,
          ...data,
        };
      });

      /**
       * Prefer weekly closed days from:
       * venues/{venueId}/auditWeeks/{weekStartKey}
       *
       * Fallback to closedDays saved directly on audit docs, because your
       * screenshot shows closedDays stored on the audit document itself.
       */
      let closedDaysObj = await loadWeeklyClosedDays(venueId, weekStartKey);

      if (!closedDaysObj) {
        closedDaysObj = getClosedDaysFromAuditDocs(snap.docs);
      }

      const openDayKeys = buildOpenDaysBetween(
        weekStartKey,
        wtdEndKey,
        closedDaysObj
      );

      const denom = openDayKeys.length;

      let venueSum = 0;
      let adminSum = 0;

      let venueAny = false;
      let adminAny = false;

      for (const dayKey of openDayKeys) {
        const audit = byDay[dayKey];

        const venueRaw = audit?.venueScore;
        const adminRaw = audit?.adminScore;

        if (venueRaw !== undefined && venueRaw !== null) {
          venueAny = true;
        }

        if (adminRaw !== undefined && adminRaw !== null) {
          adminAny = true;
        }

        venueSum += clampPercent(venueRaw ?? 0);
        adminSum += clampPercent(adminRaw ?? 0);
      }

      venueAvgWTD.value = denom ? Math.round(venueSum / denom) : 0;
      adminAvgWTD.value = denom ? Math.round(adminSum / denom) : 0;

      hasVenueAuditWTD.value = venueAny;
      hasAdminAuditWTD.value = adminAny;

      console.log("[AUDIT KPIS]", {
        venueId,
        selectedWeekEndingKey,
        weekStartKey,
        wtdEndKey,
        docsFound: snap.size,
        docIds: snap.docs.map((d) => d.id),
        openDayKeys,
        closedDaysObj,
        denomOpenDays: denom,
        venueAvgWTD: venueAvgWTD.value,
        adminAvgWTD: adminAvgWTD.value,
        hasVenueAuditWTD: hasVenueAuditWTD.value,
        hasAdminAuditWTD: hasAdminAuditWTD.value,
      });
    } catch (e) {
      error.value = e?.message || String(e);
      console.warn("[useAuditKpis] loadAuditWeekToDate failed:", e);
    } finally {
      loading.value = false;
    }
  }

  return {
    loading,
    error,
    adminAvgWTD,
    venueAvgWTD,
    hasAdminAuditWTD,
    hasVenueAuditWTD,
    loadAuditWeekToDate,
  };
}